/**
 * Telehealth API Service
 *
 * Handles telehealth sessions, encounters, and related operations
 */

import axios, { AxiosInstance } from 'axios';
import { config } from '@/src/config';
import {
  TelehealthSession,
  TelehealthSessionCreate,
  TelehealthSessionUpdate,
  TelehealthSessionListResponse,
  TelehealthEncounter,
  TelehealthEncounterCreate,
  TelehealthEncounterUpdate,
  TelehealthEncounterListResponse,
  StartSessionResponse,
  EndSessionResponse,
  EndSessionRequest,
  SessionAssessmentResponse,
  SessionStateHistoryResponse,
  TelehealthReportSummary,
  LinkAssessmentRequest,
  LinkCaseNoteRequest,
  SessionStatus,
  EncounterDocumentation,
  // Voice AI types
  VoiceAIConsentRequest,
  VoiceAIConsentResponse,
  TranscriptionResult,
  TelehealthEncounterConfig,
  EncounterConfigUpdate,
  SDOHDomainDefinition,
  TelehealthSessionWithVoiceAI,
} from '@/components/telehealth/types';

const API_URL = config.weaveinApi.baseUrl;

// ============================================================================
// AXIOS CLIENT SETUP
// ============================================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'X-CBO-ID': cboId || '',
    'Content-Type': 'application/json',
  };
};

const createClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor for auth
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (cboId) {
      config.headers['X-CBO-ID'] = cboId;
    }
    return config;
  });

  // Response error interceptor
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error('Telehealth API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        message: error.response?.data?.detail || error.message,
      });
      return Promise.reject(error);
    }
  );

  return client;
};

const client = createClient();

// ============================================================================
// SESSION API
// ============================================================================

export const telehealthSessionAPI = {
  /**
   * Create a new telehealth session from a calendar event
   */
  createSession: async (data: TelehealthSessionCreate): Promise<TelehealthSession> => {
    const response = await client.post<TelehealthSession>('/api/telehealth/sessions', data);
    return response.data;
  },

  /**
   * Get a telehealth session by ID
   */
  getSession: async (sessionId: number): Promise<TelehealthSession> => {
    const response = await client.get<TelehealthSession>(`/api/telehealth/sessions/${sessionId}`);
    return response.data;
  },

  /**
   * List telehealth sessions with filters
   */
  listSessions: async (params?: {
    status?: SessionStatus;
    member_id?: number;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<TelehealthSessionListResponse> => {
    const response = await client.get<TelehealthSessionListResponse>('/api/telehealth/sessions', {
      params,
    });
    return response.data;
  },

  /**
   * Get upcoming sessions for the current coordinator
   */
  getUpcomingSessions: async (params?: {
    days_ahead?: number;
    member_id?: number;
  }): Promise<TelehealthSession[]> => {
    const response = await client.get<TelehealthSession[]>('/api/telehealth/sessions/upcoming', {
      params,
    });
    return response.data;
  },

  /**
   * Get currently active sessions (waiting or in_progress)
   */
  getActiveSessions: async (): Promise<TelehealthSession[]> => {
    const response = await client.get<TelehealthSession[]>('/api/telehealth/sessions/active');
    return response.data;
  },

  /**
   * Get recent sessions (completed, no_show, cancelled)
   */
  getRecentSessions: async (params?: {
    days_back?: number;
    include_completed?: boolean;
  }): Promise<TelehealthSession[]> => {
    const response = await client.get<TelehealthSession[]>('/api/telehealth/sessions/recent', {
      params,
    });
    return response.data;
  },

  /**
   * Get all sessions (past and future) for grouping by member
   */
  getAllSessions: async (params?: {
    days_back?: number;
    days_ahead?: number;
    member_id?: number;
  }): Promise<TelehealthSession[]> => {
    const response = await client.get<TelehealthSession[]>('/api/telehealth/sessions/all', {
      params,
    });
    return response.data;
  },

  /**
   * Join the waiting room for a session
   */
  joinWaitingRoom: async (
    sessionId: number,
    participantType: 'member' | 'coordinator'
  ): Promise<{ message: string; session_id: number; status: string }> => {
    const response = await client.post(
      `/api/telehealth/sessions/${sessionId}/join-waiting-room?participant_type=${participantType}`
    );
    return response.data;
  },

  /**
   * Start a telehealth session
   */
  startSession: async (sessionId: number): Promise<StartSessionResponse> => {
    const response = await client.post<StartSessionResponse>(
      `/api/telehealth/sessions/${sessionId}/start`
    );
    return response.data;
  },

  /**
   * End a telehealth session
   */
  endSession: async (sessionId: number, data: EndSessionRequest): Promise<EndSessionResponse> => {
    const response = await client.post<EndSessionResponse>(
      `/api/telehealth/sessions/${sessionId}/end`,
      data
    );
    return response.data;
  },

  /**
   * Update a telehealth session
   */
  updateSession: async (
    sessionId: number,
    data: TelehealthSessionUpdate
  ): Promise<TelehealthSession> => {
    const response = await client.patch<TelehealthSession>(
      `/api/telehealth/sessions/${sessionId}`,
      data
    );
    return response.data;
  },

  /**
   * Get session state history
   */
  getSessionHistory: async (sessionId: number): Promise<SessionStateHistoryResponse[]> => {
    const response = await client.get<SessionStateHistoryResponse[]>(
      `/api/telehealth/sessions/${sessionId}/history`
    );
    return response.data;
  },

  /**
   * Get telehealth sessions for a specific member
   */
  getMemberSessions: async (
    memberId: number,
    params?: {
      include_past?: boolean;
      limit?: number;
    }
  ): Promise<TelehealthSession[]> => {
    const response = await client.get<TelehealthSession[]>(
      `/api/telehealth/member/${memberId}/sessions`,
      { params }
    );
    return response.data;
  },
};

// ============================================================================
// ENCOUNTER API
// ============================================================================

export const telehealthEncounterAPI = {
  /**
   * Create a new encounter for a telehealth session
   */
  createEncounter: async (data: TelehealthEncounterCreate): Promise<TelehealthEncounter> => {
    const response = await client.post<TelehealthEncounter>('/api/telehealth/encounters', data);
    return response.data;
  },

  /**
   * Get an encounter by ID
   */
  getEncounter: async (encounterId: number): Promise<TelehealthEncounter> => {
    const response = await client.get<TelehealthEncounter>(
      `/api/telehealth/encounters/${encounterId}`
    );
    return response.data;
  },

  /**
   * List encounters with filters
   */
  listEncounters: async (params?: {
    member_id?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<TelehealthEncounterListResponse> => {
    const response = await client.get<TelehealthEncounterListResponse>(
      '/api/telehealth/encounters',
      { params }
    );
    return response.data;
  },

  /**
   * Update encounter documentation
   */
  updateEncounter: async (
    encounterId: number,
    data: TelehealthEncounterUpdate
  ): Promise<TelehealthEncounter> => {
    const response = await client.patch<TelehealthEncounter>(
      `/api/telehealth/encounters/${encounterId}`,
      data
    );
    return response.data;
  },

  /**
   * Update only the documentation fields (convenience method)
   */
  updateDocumentation: async (
    encounterId: number,
    documentation: Partial<EncounterDocumentation>
  ): Promise<TelehealthEncounter> => {
    const response = await client.patch<TelehealthEncounter>(
      `/api/telehealth/encounters/${encounterId}`,
      { documentation }
    );
    return response.data;
  },

  /**
   * Sign and finalize an encounter
   */
  signEncounter: async (encounterId: number): Promise<TelehealthEncounter> => {
    const response = await client.post<TelehealthEncounter>(
      `/api/telehealth/encounters/${encounterId}/sign`
    );
    return response.data;
  },

  /**
   * Ensure the encounter has a progress-note form_instance from the org's
   * configured telehealth form template; returns the template sections + instance
   * so documentation can be rendered with FormRenderer.
   */
  ensureEncounterForm: async (
    encounterId: number
  ): Promise<{
    template: { id: number; name: string; sections: any[] } | null;
    instance: { id: number; status: string; field_data: Record<string, any> } | null;
  }> => {
    const response = await client.post(`/api/telehealth/encounters/${encounterId}/ensure-form`);
    return response.data;
  },

  /**
   * Link an SDOH assessment to an encounter
   */
  linkAssessment: async (
    encounterId: number,
    data: LinkAssessmentRequest
  ): Promise<SessionAssessmentResponse> => {
    const response = await client.post<SessionAssessmentResponse>(
      `/api/telehealth/encounters/${encounterId}/link-assessment`,
      data
    );
    return response.data;
  },

  /**
   * Link a case note to an encounter
   */
  linkCaseNote: async (
    encounterId: number,
    data: LinkCaseNoteRequest
  ): Promise<{ message: string; case_note_id: number }> => {
    const response = await client.post(
      `/api/telehealth/encounters/${encounterId}/link-case-note`,
      data
    );
    return response.data;
  },

  /**
   * Get encounters for a specific member
   */
  getMemberEncounters: async (
    memberId: number,
    params?: { limit?: number }
  ): Promise<TelehealthEncounter[]> => {
    const response = await client.get<TelehealthEncounter[]>(
      `/api/telehealth/member/${memberId}/encounters`,
      { params }
    );
    return response.data;
  },
};

// ============================================================================
// REPORTING API
// ============================================================================

export const telehealthReportingAPI = {
  /**
   * Get telehealth report summary for a date range
   */
  getReportSummary: async (
    startDate: string,
    endDate: string
  ): Promise<TelehealthReportSummary> => {
    const response = await client.get<TelehealthReportSummary>('/api/telehealth/reports/summary', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
};

// ============================================================================
// VOICE AI & CONSENT API
// ============================================================================

export const telehealthVoiceAIAPI = {
  /**
   * Get consent status for a session
   */
  getConsentStatus: async (sessionId: number): Promise<VoiceAIConsentResponse> => {
    const response = await client.get<VoiceAIConsentResponse>(
      `/api/telehealth/sessions/${sessionId}/consent`
    );
    return response.data;
  },

  /**
   * Record consent decision for a session
   */
  recordConsent: async (
    sessionId: number,
    data: VoiceAIConsentRequest
  ): Promise<VoiceAIConsentResponse> => {
    const response = await client.post<VoiceAIConsentResponse>(
      `/api/telehealth/sessions/${sessionId}/consent`,
      data
    );
    return response.data;
  },

  /**
   * Start voice AI transcription for a session
   */
  startTranscription: async (sessionId: number): Promise<{ transcription_id: number; status: string }> => {
    const response = await client.post(`/api/telehealth/sessions/${sessionId}/transcription/start`);
    return response.data;
  },

  /**
   * Stop voice AI transcription for a session
   */
  stopTranscription: async (sessionId: number): Promise<TranscriptionResult> => {
    const response = await client.post<TranscriptionResult>(
      `/api/telehealth/sessions/${sessionId}/transcription/stop`
    );
    return response.data;
  },

  /**
   * Get transcription results for a session
   */
  getTranscription: async (sessionId: number): Promise<TranscriptionResult> => {
    const response = await client.get<TranscriptionResult>(
      `/api/telehealth/sessions/${sessionId}/transcription`
    );
    return response.data;
  },

  /**
   * Apply AI suggested notes to encounter
   */
  applyTranscriptionSuggestions: async (
    sessionId: number,
    fields: string[]
  ): Promise<{ applied_fields: string[] }> => {
    const response = await client.post(`/api/telehealth/sessions/${sessionId}/transcription/apply`, {
      fields,
    });
    return response.data;
  },

  /**
   * Get session with voice AI configuration
   */
  getSessionWithConfig: async (sessionId: number): Promise<TelehealthSessionWithVoiceAI> => {
    const response = await client.get<TelehealthSessionWithVoiceAI>(
      `/api/telehealth/sessions/${sessionId}/with-config`
    );
    return response.data;
  },
};

// ============================================================================
// ENCOUNTER CONFIG API
// ============================================================================

export const telehealthConfigAPI = {
  /**
   * Get encounter configuration for the current org
   */
  getConfig: async (programId?: number): Promise<TelehealthEncounterConfig | null> => {
    const response = await client.get<TelehealthEncounterConfig>('/api/telehealth/config', {
      params: programId ? { program_id: programId } : undefined,
    });
    return response.data;
  },

  /**
   * Update encounter configuration
   */
  updateConfig: async (
    data: EncounterConfigUpdate,
    programId?: number
  ): Promise<TelehealthEncounterConfig> => {
    const response = await client.put<TelehealthEncounterConfig>('/api/telehealth/config', data, {
      params: programId ? { program_id: programId } : undefined,
    });
    return response.data;
  },

  /**
   * Create or set the encounter form template
   */
  setFormTemplate: async (
    formTemplateId: number,
    programId?: number
  ): Promise<TelehealthEncounterConfig> => {
    const response = await client.post<TelehealthEncounterConfig>(
      '/api/telehealth/config/form-template',
      { form_template_id: formTemplateId },
      { params: programId ? { program_id: programId } : undefined }
    );
    return response.data;
  },

  /**
   * Get available SDOH domains
   */
  getSdohDomains: async (): Promise<SDOHDomainDefinition[]> => {
    const response = await client.get<SDOHDomainDefinition[]>('/api/telehealth/sdoh-domains');
    return response.data;
  },
};

// ============================================================================
// TWILIO VIDEO API
// ============================================================================

export interface VideoRoomResponse {
  room_sid: string;
  room_name: string;
  status: string;
  video_provider: string;
  max_participants: number;
  created_at?: string;
}

export interface VideoTokenResponse {
  token: string;
  room_name: string;
  identity: string;
  expires_in: number;
}

export interface VideoParticipant {
  id: number;
  telehealth_session_id: number;
  participant_identity: string;
  participant_sid?: string;
  participant_type: string;
  user_id?: number;
  member_id?: number;
  joined_at?: string;
  left_at?: string;
  duration_seconds?: number;
  connection_quality?: string;
  network_quality_local?: number;
  network_quality_remote?: number;
}

export interface VideoParticipantListResponse {
  participants: VideoParticipant[];
  total: number;
}

export interface EndVideoRoomResponse {
  room_sid: string;
  status: string;
  message: string;
}

export const telehealthVideoAPI = {
  /**
   * Create a Twilio video room for a session
   */
  createVideoRoom: async (
    sessionId: number,
    options?: {
      room_type?: 'go' | 'peer-to-peer' | 'group-small' | 'group';
      max_participants?: number;
      record_participants?: boolean;
    }
  ): Promise<VideoRoomResponse> => {
    const response = await client.post<VideoRoomResponse>(
      `/api/telehealth/sessions/${sessionId}/video/room`,
      options || {}
    );
    return response.data;
  },

  /**
   * Get access token for coordinator to join video room
   */
  getVideoToken: async (
    sessionId: number,
    participantName?: string
  ): Promise<VideoTokenResponse> => {
    const response = await client.post<VideoTokenResponse>(
      `/api/telehealth/sessions/${sessionId}/video/token`,
      participantName ? { participant_name: participantName } : {}
    );
    return response.data;
  },

  /**
   * Access token for the signed-in MEMBER to join their own visit (TRUS-688).
   * The member comes from the login, never from the request; the server returns
   * 404 unless this login is the visit's member and the join window is open
   * (15 minutes before the start until the visit ends).
   */
  getMemberVideoToken: async (sessionId: number): Promise<VideoTokenResponse> => {
    const response = await client.post<VideoTokenResponse>(
      `/api/telehealth/sessions/${sessionId}/video/member-token`,
      {}
    );
    return response.data;
  },

  /**
   * End the video room and disconnect all participants
   */
  endVideoRoom: async (sessionId: number): Promise<EndVideoRoomResponse> => {
    const response = await client.post<EndVideoRoomResponse>(
      `/api/telehealth/sessions/${sessionId}/video/end`
    );
    return response.data;
  },

  /**
   * Get list of participants in the video call
   */
  getVideoParticipants: async (sessionId: number): Promise<VideoParticipantListResponse> => {
    const response = await client.get<VideoParticipantListResponse>(
      `/api/telehealth/sessions/${sessionId}/video/participants`
    );
    return response.data;
  },

  /**
   * Update video quality metrics for the current participant
   */
  updateVideoQuality: async (
    sessionId: number,
    quality: {
      network_quality_local?: number;
      network_quality_remote?: number;
      connection_quality?: 'excellent' | 'good' | 'fair' | 'poor';
    }
  ): Promise<{ status: string }> => {
    const response = await client.patch(
      `/api/telehealth/sessions/${sessionId}/video/quality`,
      quality
    );
    return response.data;
  },
};

// ============================================================================
// COMBINED API EXPORT
// ============================================================================

export const telehealthAPI = {
  sessions: telehealthSessionAPI,
  encounters: telehealthEncounterAPI,
  reporting: telehealthReportingAPI,
  voiceAI: telehealthVoiceAIAPI,
  config: telehealthConfigAPI,
  video: telehealthVideoAPI,
};

export default telehealthAPI;
